import dayjs from 'dayjs';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import BaseDatePicker from './BaseDatePicker';

function get(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * ControlledDatePicker
 *
 * React Hook Form wrapper around BaseDatePicker.
 *
 * RHF VALUE CONTRACT
 * - Stored value: ISO date string (YYYY-MM-DD) | null
 *
 * Mapping:
 * - RHF → BaseDatePicker: dayjs(value)
 * - BaseDatePicker → RHF: value.format('YYYY-MM-DD')
 *
 * @param {Object} props
 * @param {string} props.name - Field name registered with react-hook-form.
 * @param {Object} [props.rules] - Validation rules passed directly to RHF Controller.
 * @param {boolean} [props.showErrorInline=true] - Controls whether validation errors are rendered inline or delegated to a parent error summary.
 *
 * @returns {React.ReactElement}
 */
export default function ControlledDatePicker({ name, rules = {}, showErrorInline = true, ...rest }: { name: any; rules?: any; showErrorInline?: boolean; [key: string]: any }) {
  const { control, clearErrors } = useFormContext();
  const { errors } = useFormState({ name });
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const normalizedValue = dayjs.isDayjs(field.value)
          ? field.value
          : typeof field.value === 'string'
            ? dayjs(field.value)
            : null;

        return (
          <BaseDatePicker
            {...rest}
            value={normalizedValue}
            onChange={(value) => {
              field.onChange(value ? value.format('YYYY-MM-DD') : null);
              if (error) clearErrors(name);
            }}
            error={showErrorInline && Boolean(error)}
            errorMessage={showErrorInline ? error?.message : undefined}
            showErrorInline={showErrorInline}
          />
        );
      }}
    />
  );
}
